import { useEffect } from 'react';
import * as Sqrl from 'squirrelly';
import moment from 'moment';
import { Timestamp } from 'firebase/firestore';

const debugHelpers = false;

const useSquirrellyHelpers = () => {
  /* defined additional markup helpers */
  useEffect(() => {
    // just returns an empty string if the first argument is undefined
    Sqrl.helpers.define('val', function(args) {
      if (args.params.length === 0) {
        return 'val.invalid_args';
      }
      const value = args.params[0];
      return value === undefined ? '' : value;
    });

    Sqrl.helpers.define('parseDate', function(args) {
      if (args.params.length === 0) {
        return 'parseDate.invalid_args';
      }
      const timestamp = args.params[0];
      let format = 'MMM Do YYYY';
      if (args.params.length > 1) {
        format = args.params[1];
      }
      const ts = new Timestamp(
        timestamp?.seconds || 0,
        timestamp?.nanoseconds || 0
      );

      try {
        return moment(ts.toDate()).format(format);
      } catch {
        return 'parseDate.invalid_timestamp or invalid_format';
      }
    });

    Sqrl.helpers.define('format', function(args) {
      if (args.params.length === 0) {
        return 'format.invalid_args';
      }
      const value = args.params[0];

      let format = '';
      if (args.params.length > 1) {
        format = args.params[1];
      } else {
        return value;
      }

      if (value === null || value === undefined) {
        return value;
      }

      switch (format) {
        case 'upper':
          return value.toString().toUpperCase();
        case 'lower':
          return value.toString().toLowerCase();
        case 'proper':
          return value
            .toString()
            .split(' ')
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ');
        default:
          return value;
      }
    });

    /*
      Returns conditional content if the supplied value is null, undefined or empty string

      {{@empty(value) /}}
      {{#empty}}
        // empty
      {{#else}}
        // not empty
      {{/empty}}

    */
    Sqrl.helpers.define('empty', function(args, blocks, content) {
      if (debugHelpers) {
        console.log('empty', { blocks });
      }

      if (args.params.length !== 1) {
        return 'empty.invalid_args';
      }

      const value = args.params[0];
      const elseBlock = blocks?.find(b => b.name?.toLowerCase() === 'else');
      const emptyBlock = blocks?.find(b => b.name?.toLowerCase() === 'empty');

      const empty =
        value === undefined ||
        value === null ||
        (value?.toString && value.toString().trim().length === 0);

      try {
        if (empty) {
          return emptyBlock ? emptyBlock.exec() : '';
        } else if (elseBlock) {
          return elseBlock.exec();
        } else {
          return '';
        }
      } catch (error) {
        return 'empty error' + error;
      }
    });

    /*
      Returns conditional content if the supplied value is not undefined, null or empty string

      {{@notEmpty(value) /}}
      {{#notEmpty}}
        // is not empty
      {{#else}}
        // is empty
      {{/notEmpty}}

    */
    Sqrl.helpers.define('notEmpty', function(args, blocks, content) {
      if (debugHelpers) {
        console.log('notEmpty', { blocks });
      }
      if (args.params.length !== 1) {
        return 'notEmpty.invalid_args';
      }

      const elseBlock = blocks?.find(b => b.name?.toLowerCase() === 'else');
      const notEmptyBlock = blocks?.find(
        b => b.name?.toLowerCase() === 'notempty'
      );
      const value = args.params[0];

      const empty =
        value === undefined ||
        value === null ||
        (value?.toString && value.toString().trim().length === 0);

      try {
        if (!empty) {
          return notEmptyBlock ? notEmptyBlock.exec() : '';
        } else if (elseBlock) {
          return elseBlock.exec();
        } else {
          return '';
        }
      } catch (error) {
        return 'notEmpty error' + error;
      }
    });

    /*
      Returns conditional content if an object from an array matches a field and value

      {{@exists(it.local.somearray,field,value) /}}
      {{#exists}}
        // exists content
      {{#else}}
        // does not exists
      {{/exists}}

    */
    Sqrl.helpers.define('exists', function(args, blocks, content) {
      if (debugHelpers) {
        console.log('exists', { blocks });
      }
      if (args.params.length !== 3) {
        return 'exists.invalid_args';
      }
      const target = args.params[0];
      const field = args.params[1];
      const value = args.params[2];

      const elseBlock = blocks?.find(b => b.name?.toLowerCase() === 'else');
      const existsBlock = blocks?.find(b => b.name?.toLowerCase() === 'exists');

      if (
        !Array.isArray(target) ||
        target === undefined ||
        target === null ||
        target?.length === 0
      ) {
        return elseBlock ? elseBlock.exec() : '';
      }

      const exists = target.find(t => t[field] === value) !== undefined;

      try {
        if (exists) {
          return existsBlock ? existsBlock.exec() : '';
        } else if (elseBlock) {
          return elseBlock.exec();
        } else {
          return '';
        }
      } catch (error) {
        return 'exists error' + error;
      }
    });

    /*
      Returns conditional content based on whether a date is within so many minutes, hours, days, weeks, months or years
      
      Example:
      
            {{ isWithin(it?.user?.expiryDate,3,'months') }}
             {{#whenWithin}}
                // the content when within
             {{#whenNotWithin}} or {{#else}}
                // the content when notwithin
             {{/isWithin}}
    */
    Sqrl.helpers.define('isWithin', function(args, blocks, content) {
      if (debugHelpers) {
        console.log('isWithin', { blocks });
      }
      if (args.params.length === 0) {
        return 'isWithin.invalid_args';
      }

      const timestamp = args.params[0];
      let period = 'hours'; // Default period
      let amount = 1; // Default amount

      // Check if a second argument (amount) is passed
      if (args.params.length > 1) {
        amount = args.params[1];
      }

      // Check if a third argument (period type) is passed
      if (args.params.length > 2) {
        period = args.params[2];
      }

      const ts = new Timestamp(
        timestamp?.seconds || 0,
        timestamp?.nanoseconds || 0
      );

      const dateTime = ts.toDate();

      const whenWithinBlock = blocks?.find(
        b => b.name?.toLowerCase() === 'whenwithin'
      );
      const elseBlock = blocks?.find(
        b =>
          b.name?.toLowerCase() === 'whennotwithin' ||
          b.name?.toLowerCase() === 'else'
      );

      try {
        const now = moment();
        const isWithinPeriod =
          now.isSame(dateTime, period) ||
          now.isAfter(moment(dateTime).subtract(amount, period));

        if (isWithinPeriod) {
          return whenWithinBlock ? whenWithinBlock.exec() : '';
        } else if (elseBlock) {
          return elseBlock.exec();
        } else {
          return '';
        }
      } catch (error) {
        return 'isWithin error' + error;
      }
    });

    /*
      Returns conditional content based on whether a date is at least in the past or equal to the current date.
      
      Example:
      
            {{ isInPast(it?.user?.expiryDate,3,'months') }}
                // this content will be ignored
             {{#whenInPast}} or {{#inPast}}
                // the content for when the date is in the past
             {{#whenNotInPast}} or {{#else}}
                // the content for when the date is not in the past
             {{/isAfter}}
    */
    Sqrl.helpers.define('isInPast', function(args, blocks, content) {
      if (debugHelpers) {
        console.log('isInPast', { blocks });
      }

      if (args.params.length === 0) {
        return 'isInPast.invalid_args';
      }

      const timestamp = args.params[0];

      const ts = new Timestamp(
        timestamp?.seconds || 0,
        timestamp?.nanoseconds || 0
      );

      const dateTime = ts.toDate();

      const inPastBlock = blocks?.find(
        b =>
          b.name?.toLowerCase() === 'wheninpast' ||
          b.name.toLowerCase() === 'inpast'
      );
      const elseBlock = blocks?.find(
        b =>
          b.name?.toLowerCase() === 'whennotinpast' ||
          b.name?.toLowerCase() === 'else'
      );

      try {
        const now = moment();
        const isInPast = now.isAfter(dateTime);
        if (isInPast) {
          return inPastBlock ? inPastBlock.exec() : '';
        } else if (elseBlock) {
          return elseBlock.exec();
        } else {
          return '';
        }
      } catch (error) {
        return 'isInPast error' + error;
      }
    });
  }, []);
};

export default useSquirrellyHelpers;
